/*
Copyright (C) 2023-2026 QuantumNous

This program is free software: you can redistribute it and/or modify
it under the terms of the GNU Affero General Public License as
published by the Free Software Foundation, either version 3 of the
License, or (at your option) any later version.

This program is distributed in the hope that it will be useful,
but WITHOUT ANY WARRANTY; without even the implied warranty of
MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE. See the
GNU Affero General Public License for more details.

You should have received a copy of the GNU Affero General Public License
along with this program. If not, see <https://www.gnu.org/licenses/>.

For commercial licensing, please contact support@quantumnous.com
*/
import { useMemo, useState } from 'react'
import { useNavigate } from '@tanstack/react-router'
import { KeyRound, LogOut, Settings, Wallet } from 'lucide-react'
import { useTranslation } from 'react-i18next'
import { useAuthStore } from '@/stores/auth-store'
import { getUserAvatarFallback, getUserAvatarStyle } from '@/lib/avatar'
import { useFormatBalance } from '@/lib/use-format-balance'
import { cn } from '@/lib/utils'
import useDialogState from '@/hooks/use-dialog'
import { useUserDisplay } from '@/hooks/use-user-display'
import { Avatar, AvatarFallback } from '@/components/ui/avatar'
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover'
import { SignOutDialog } from '@/components/sign-out-dialog'

/**
 * Bottom-of-sidebar user identity chip that opens an upward popover.
 *
 * Reproduces the design in popup.png:
 *   Trigger (always visible): bordered rounded chip — round avatar (initials)
 *     + display name (bold) + email (muted).
 *   Popover (opens UPWARD, side='top'):
 *     1. Header — avatar + name + email.
 *     2. Account Balance row — real formatted balance on the right (e.g. ¥ 288.00).
 *     3. Account Settings → /profile
 *     4. API Tokens → /keys
 *     5. Top-up Center → /wallet
 *     6. Log out — RED, opens the shared SignOutDialog (existing logout action).
 *
 * All identity + balance data comes from the backend (auth store + status-driven
 * currency formatting, the same `useFormatBalance` the wallet/dashboard use).
 */
export function NoyaProfileFooter() {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const [open, setOpen] = useState(false)
  const [signOutOpen, setSignOutOpen] = useDialogState()

  const user = useAuthStore((state) => state.auth.user)
  const { displayName, secondaryText } = useUserDisplay(user)
  const formatBalance = useFormatBalance()

  const avatarName = user?.username || displayName
  const avatarFallback = getUserAvatarFallback(avatarName)
  const avatarStyle = useMemo(() => getUserAvatarStyle(avatarName), [avatarName])

  // Real balance — identical source/formatting to wallet-stats-card.
  const balance = formatBalance(user?.quota ?? 0)

  const go = (to: string) => {
    setOpen(false)
    navigate({ to })
  }

  const menuRowClass =
    'flex w-full items-center gap-2.5 rounded-md px-2.5 py-2 text-left text-[13px] transition-colors hover:bg-accent focus-visible:bg-accent outline-none'

  return (
    <>
      <Popover open={open} onOpenChange={setOpen}>
        {/* ── Trigger chip ── */}
        <PopoverTrigger
          render={
            <button
              type='button'
              aria-label={t('Account Settings')}
              className={cn(
                'flex w-full items-center gap-2.5 rounded-xl border px-2.5 py-2 text-left',
                'border-border bg-background transition-colors',
                'hover:bg-accent data-popup-open:bg-accent',
                'focus-visible:ring-ring/40 outline-none focus-visible:ring-2'
              )}
            />
          }
        >
          <Avatar className='size-[2.125rem] shrink-0'>
            <AvatarFallback
              className='text-xs font-semibold text-white'
              style={avatarStyle}
            >
              {avatarFallback}
            </AvatarFallback>
          </Avatar>
          <div className='min-w-0 flex-1 group-data-[collapsible=icon]:hidden'>
            <p className='text-foreground truncate text-[13px] font-semibold'>
              {displayName}
            </p>
            {secondaryText && (
              <p className='text-muted-foreground truncate text-[11px]'>
                {secondaryText}
              </p>
            )}
          </div>
        </PopoverTrigger>

        {/* ── Popover (opens upward) ── */}
        <PopoverContent
          side='top'
          align='start'
          sideOffset={8}
          className='w-(--anchor-width) min-w-[15rem] gap-0 p-1.5'
        >
          {/* 1. Header */}
          <div className='flex items-center gap-2.5 px-1.5 py-2'>
            <Avatar className='size-9 shrink-0'>
              <AvatarFallback
                className='text-xs font-semibold text-white'
                style={avatarStyle}
              >
                {avatarFallback}
              </AvatarFallback>
            </Avatar>
            <div className='min-w-0 flex-1'>
              <p className='text-foreground truncate text-[13px] font-semibold'>
                {displayName}
              </p>
              {secondaryText && (
                <p className='text-muted-foreground truncate text-[11px]'>
                  {secondaryText}
                </p>
              )}
            </div>
          </div>

          <hr className='border-border my-1 border-0 border-t' aria-hidden='true' />

          {/* 2. Account Balance */}
          <div className='flex items-center justify-between px-2.5 py-2 text-[13px]'>
            <span className='text-muted-foreground'>{t('Account Balance')}</span>
            <span className='text-foreground font-semibold'>{balance}</span>
          </div>

          <hr className='border-border my-1 border-0 border-t' aria-hidden='true' />

          {/* 3. Account Settings → /profile */}
          <button
            type='button'
            className={cn(menuRowClass, 'text-foreground')}
            onClick={() => go('/profile')}
          >
            <Settings className='text-muted-foreground size-4 shrink-0' />
            {t('Account Settings')}
          </button>

          {/* 4. API Tokens → /keys */}
          <button
            type='button'
            className={cn(menuRowClass, 'text-foreground')}
            onClick={() => go('/keys')}
          >
            <KeyRound className='text-muted-foreground size-4 shrink-0' />
            {t('API Tokens')}
          </button>

          {/* 5. Top-up Center → /wallet */}
          <button
            type='button'
            className={cn(menuRowClass, 'text-foreground')}
            onClick={() => go('/wallet')}
          >
            <Wallet className='text-muted-foreground size-4 shrink-0' />
            {t('Top-up Center')}
          </button>

          <hr className='border-border my-1 border-0 border-t' aria-hidden='true' />

          {/* 6. Log out — red / destructive */}
          <button
            type='button'
            className={cn(
              menuRowClass,
              'text-destructive hover:bg-destructive/10 focus-visible:bg-destructive/10'
            )}
            onClick={() => {
              setOpen(false)
              setSignOutOpen(true)
            }}
          >
            <LogOut className='size-4 shrink-0' />
            {t('Log out')}
          </button>
        </PopoverContent>
      </Popover>

      <SignOutDialog open={!!signOutOpen} onOpenChange={setSignOutOpen} />
    </>
  )
}
